/**
 * Client-side image compression using Canvas API.
 *
 * Resizes images to a maximum dimension and re-encodes as JPEG,
 * drastically reducing file size (e.g. 30MB phone photo -> ~300KB).
 */

export interface CompressImageOptions {
  maxDimension?: number
  quality?: number
}

export async function compressImage(file: File, options: CompressImageOptions = {}): Promise<File> {
  const { maxDimension = 1920, quality = 0.8 } = options

  if (!file || !file.type.startsWith('image/')) {
    throw new Error('Invalid image file')
  }

  const bitmap = await createImageBitmap(file)
  const { width, height } = bitmap

  let targetWidth = width
  let targetHeight = height

  if (width > maxDimension || height > maxDimension) {
    if (width > height) {
      targetWidth = maxDimension
      targetHeight = Math.round((height / width) * maxDimension)
    } else {
      targetHeight = maxDimension
      targetWidth = Math.round((width / height) * maxDimension)
    }
  }

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to acquire 2D canvas context')
  }
  ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality)
  })

  if (!blob) {
    throw new Error('Canvas toBlob returned null')
  }

  const compressedName = file.name.replace(/\.[^.]+$/, '.jpg')
  return new File([blob], compressedName, { type: 'image/jpeg' })
}
